import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
  FormsModule,
} from '@angular/forms';

import { OrganizationService } from '../../../../core/services/organization/organization.service';
import { FacilityService } from '../../../../core/services/organization/facility.service';
import {
  UpdateOrganizationRequest,
  OrganizationAvatarUploadResponse,
} from '../../../../core/models/organization/organization.model';
import { BaseApiResponse } from '../../../../core/models/api/api.model';
import {
  CreateFacilityRequest,
  UpdateFacilityRequest,
} from '../../../../core/models/organization/facility.model';
import { ToastService } from '../../../../core/services/toast.service';
import { FullscreenOverlayComponent } from '../../../components/fullscreen-overlay/fullscreen-overlay.component';
import { TimezoneService } from '../../../../core/services/organization/timezone.service';

import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import {
  CountryISO,
  SearchCountryField,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import { PhoneInputDirective } from '../../../../shared/directives/phone-input.directive';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { getCountryCodeFromDialCode } from '../../../../utils/country-code-utils';

@Component({
  selector: 'app-organization-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    NgxIntlTelInputModule,
    FullscreenOverlayComponent,
    PhoneInputDirective,
    SelectComponent,
  ],
  templateUrl: './organization-detail.component.html',
  styleUrls: ['./organization-detail.component.scss'],
})
export class OrganizationDetailComponent implements OnInit {
  organizationId: string | null = null;
  isLoading = true;
  isSaving = false;
  isOverlayLoading = false;
  organizationAvatar: string | null = null;

  orgForm!: FormGroup;
  tempFacilityForm!: FormGroup;
  editingFacilityIndex: number | null = null;
  facilityToDeleteIndex: number | null = null;

  timeZones: { name: string; label: string }[] = [];

  @ViewChild('openFacilityModalBtn')
  openFacilityModalBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('closeFacilityModalBtn')
  closeFacilityModalBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('openDeleteFacilityModalBtn')
  openDeleteFacilityModalBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('closeDeleteFacilityModalBtn')
  closeDeleteFacilityModalBtn!: ElementRef<HTMLButtonElement>;

  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;
  PhoneNumberFormat = PhoneNumberFormat;
  defaultOrgCountry: CountryISO = CountryISO.Turkey;
  defaultFacilityCountry: CountryISO = CountryISO.Turkey;

  constructor(
    private route: ActivatedRoute,
    private organizationService: OrganizationService,
    private facilityService: FacilityService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private timezoneService: TimezoneService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.timeZones = this.timezoneService.getAllTimezones();
    this.initForms();

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.organizationId = idParam;
      this.getDetail(this.organizationId);
    } else {
      // No ID provided, stop loading
      this.isLoading = false;
      this.isOverlayLoading = false;
    }

    this.orgForm
      .get('isAssessmentActive')
      ?.valueChanges.subscribe((newValue: boolean) => {
        this.orgForm.patchValue(
          {
            isAssessmentActive: newValue,
          },
          { emitEvent: false },
        );
      });
  }

  private initForms(): void {
    this.orgForm = this.fb.group({
      id: [''],
      name: ['', Validators.required],
      customerCode: ['', Validators.required],
      contactPerson: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
      address: [''],
      timeZone: [null, Validators.required],
      countryCode: [''],
      organizationStatus: [null],
      isAssessmentActive: [false],
      facilities: this.fb.array([]),
    });
    this.tempFacilityForm = this.fb.group({
      id: [null],
      name: ['', Validators.required],
      contactPerson: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
      address: ['', Validators.required],
      timeZone: [null, Validators.required],
    });
  }

  get facilitiesFormArray(): FormArray {
    return this.orgForm.get('facilities') as FormArray;
  }

  getDetail(id: string): void {
    this.isLoading = true;
    this.isOverlayLoading = true;

    this.organizationService.getOrganizationById(id).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.isOverlayLoading = false;
        const data = res.data as any;

        if (data) {
          const { facilities, ...orgData } = data;

          let formattedPhone = orgData.phone;
          if (orgData.phone && orgData.countryCode) {
            try {
              const phoneNumber = parsePhoneNumberFromString(
                orgData.phone.toString(),
                orgData.countryCode.toUpperCase() as CountryCode,
              );
              if (phoneNumber) formattedPhone = phoneNumber.number;
            } catch (e) {
              console.error(e);
            }
          }

          this.orgForm.patchValue({
            ...orgData,
            phone: formattedPhone,
            isAssessmentActive: data.isAssessmentActive,
          });
          
          // Extract country code from organization phone number
          if (orgData.countryCode) {
            const countryCode = getCountryCodeFromDialCode(orgData.countryCode);
            if (countryCode) {
              this.defaultOrgCountry = countryCode as CountryISO;
            }
          }

          this.facilitiesFormArray.clear();
          if (facilities && Array.isArray(facilities)) {
            facilities.forEach((facility: any) => {
              this.facilitiesFormArray.push(
                this.createFacilityFormGroup(facility),
              );
            });
          }

          if (data.avatarFolderPath && this.organizationId) {
            this.loadOrganizationAvatar(this.organizationId);
          }
        }
      },
      error: () => {
        this.isLoading = false;
        this.isOverlayLoading = false;
      },
    });
  }

  private createFacilityFormGroup(facility: any): FormGroup {
    return this.fb.group({
      id: [facility.id],
      name: [facility.name],
      contactPerson: [facility.contactPerson],
      email: [facility.email],
      phone: [facility.phone],
      address: [facility.address],
      timeZone: [facility.timeZone],
      countryCode: [facility.countryCode],
    });
  }

  openFacilityModal(): void {
    if (!this.organizationId) {
      this.toastService.showToast('Please save organization first', 'warning');
      return;
    }
    this.editingFacilityIndex = null;
    this.tempFacilityForm.reset();
    this.defaultFacilityCountry = CountryISO.Turkey;

    const orgTimeZone = this.orgForm.get('timeZone')?.value;
    if (orgTimeZone) {
      this.tempFacilityForm.patchValue({ timeZone: orgTimeZone });
    }

    this.openFacilityModalBtn.nativeElement.click();
  }

  editFacility(index: number): void {
    this.editingFacilityIndex = index;
    const currentFacility = this.facilitiesFormArray.at(index).value;
    this.tempFacilityForm.patchValue({
      ...currentFacility,
      phone: currentFacility.phone,
    });
    
    // Extract country code from facility phone number
    if (currentFacility.countryCode) {
      const countryCode = getCountryCodeFromDialCode(currentFacility.countryCode);
      if (countryCode) {
        this.defaultFacilityCountry = countryCode as CountryISO;
      }
    } else {
      this.defaultFacilityCountry = CountryISO.Turkey;
    }
    
    this.openFacilityModalBtn.nativeElement.click();
  }

  saveFacilityFromModal(): void {
    if (this.tempFacilityForm.invalid) {
      this.tempFacilityForm.markAllAsTouched();
      return;
    }

    if (!this.organizationId) return;

    this.isOverlayLoading = true;
    const formValue = this.tempFacilityForm.value;

    let finalPhone = '';
    let finalCountryCode = '';

    const phoneInput = formValue.phone;
    if (phoneInput && typeof phoneInput === 'object') {
      finalPhone = phoneInput.number
        ? phoneInput.number.replace(/\D/g, '')
        : '';
      finalCountryCode = phoneInput.dialCode;
    } else if (typeof phoneInput === 'string' && phoneInput.trim() !== '') {
      const parsed = parsePhoneNumberFromString(phoneInput);
      if (parsed) {
        finalPhone = parsed.nationalNumber;
        finalCountryCode = '+' + parsed.countryCallingCode;
      } else {
        finalPhone = phoneInput;
      }
    }

    const payloadData = {
      name: formValue.name,
      contactPerson: formValue.contactPerson || '',
      email: formValue.email || '',
      address: formValue.address || '',
      timeZone: formValue.timeZone,
      phone: finalPhone,
      countryCode: finalCountryCode,
      organizationId: this.organizationId,
    };

    if (this.editingFacilityIndex !== null) {
      const updateRequest: UpdateFacilityRequest = {
        id: formValue.id,
        ...payloadData,
      };

      this.facilityService.updateFacility(updateRequest).subscribe({
        next: (res: any) => {
          if (res.isSuccess) {
            this.toastService.showToast(
              'Facility updated successfully',
              'success',
            );

            const updatedControl = this.createFacilityFormGroup({
              ...updateRequest,
              phone: payloadData.phone,
            });
            this.facilitiesFormArray.removeAt(this.editingFacilityIndex!);
            this.facilitiesFormArray.insert(
              this.editingFacilityIndex!,
              updatedControl,
            );
            this.closeFacilityModalBtn.nativeElement.click();
          } else {
            this.toastService.showToast(
              res.message || 'Failed to update facility',
              'error',
            );
          }
          this.isOverlayLoading = false;
        },
        error: () => {
          this.isOverlayLoading = false;
        },
      });
    } else {
      const createRequest: CreateFacilityRequest = {
        ...payloadData,
      };

      this.facilityService.createFacility(createRequest).subscribe({
        next: (res: any) => {
          if (res.isSuccess) {
            this.toastService.showToast(
              'Facility added successfully',
              'success',
            );
            const newId = res.data ? res.data.id : null;

            this.facilitiesFormArray.push(
              this.createFacilityFormGroup({
                id: newId,
                ...createRequest,
                phone: payloadData.phone,
              }),
            );
            this.closeFacilityModalBtn.nativeElement.click();
          } else {
            this.toastService.showToast(
              res.message || 'Failed to add facility',
              'error',
            );
          }
          this.isOverlayLoading = false;
        },
        error: () => {
          this.isOverlayLoading = false;
        },
      });
    }
  }

  promptDeleteFacility(index: number): void {
    const facility = this.facilitiesFormArray.at(index).value;
    if (!facility.id) {
      this.facilitiesFormArray.removeAt(index);
      return;
    }
    this.facilityToDeleteIndex = index;
    this.openDeleteFacilityModalBtn.nativeElement.click();
  }

  confirmDeleteFacility(): void {
    if (this.facilityToDeleteIndex === null) return;

    const index = this.facilityToDeleteIndex;
    const facility = this.facilitiesFormArray.at(index).value;

    this.closeDeleteFacilityModalBtn.nativeElement.click();
    this.isOverlayLoading = true;

    this.facilityService.deleteFacility(facility.id).subscribe({
      next: (res: any) => {
        if (res.isSuccess) {
          this.facilitiesFormArray.removeAt(index);
          this.toastService.showToast(
            'Facility deleted successfully',
            'success',
          );
        } else {
          this.toastService.showToast(
            res.message || 'Failed to delete facility',
            'error',
          );
        }
        this.isOverlayLoading = false;
        this.facilityToDeleteIndex = null;
      },
      error: () => {
        this.isOverlayLoading = false;
        this.toastService.showToast('Failed to delete facility', 'error');
        this.facilityToDeleteIndex = null;
      },
    });
  }

  onSubmit(): void {
    if (this.orgForm.invalid) {
      this.orgForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.isOverlayLoading = true;

    const raw = this.orgForm.getRawValue();
    const phoneInput = raw.phone;

    let finalPhone = null;
    let finalCountryCode = null;

    if (phoneInput && typeof phoneInput === 'object') {
      const rawNumber = phoneInput.number
        ? phoneInput.number.replace(/\D/g, '')
        : '';
      finalPhone = rawNumber;
      finalCountryCode = phoneInput.dialCode;
    } else if (typeof phoneInput === 'string') {
      const parsed = parsePhoneNumberFromString(phoneInput);
      if (parsed) {
        finalPhone = parsed.nationalNumber;
        finalCountryCode = '+' + parsed.countryCallingCode;
      } else {
        finalPhone = phoneInput;
      }
    }

    const updateRequest: UpdateOrganizationRequest = {
      id: raw.id,
      name: raw.name,
      customerCode: raw.customerCode,
      contactPerson: raw.contactPerson,
      email: raw.email,
      address: raw.address,
      timeZone: raw.timeZone,
      phone: finalPhone,
      countryCode: finalCountryCode,
      isAssessmentActive: raw.isAssessmentActive,
    };

    this.organizationService.updateOrganization(updateRequest).subscribe({
      next: (res: any) => {
        if (res.isSuccess) {
          this.orgForm.markAsPristine();
          this.toastService.showToast(
            'Organization updated successfully',
            'success',
          );
        } else {
          this.toastService.showToast(res.message || 'Update failed', 'error');
        }
        this.isSaving = false;
        this.isOverlayLoading = false;
      },
      error: () => {
        this.isSaving = false;
        this.isOverlayLoading = false;
      },
    });
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.orgForm.get(controlName);
    return control
      ? control.invalid && (control.touched || control.dirty)
      : false;
  }

  isModalControlInvalid(controlName: string): boolean {
    const control = this.tempFacilityForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  private loadOrganizationAvatar(organizationId: string): void {
    this.organizationService.getAvatar(organizationId).subscribe({
      next: (res) => {
        if (res.isSuccess && res.data?.avatar) {
          this.organizationAvatar = 'data:image/png;base64,' + res.data.avatar;
        }
      },
      error: () => {
        console.debug(
          'Failed to load avatar for organization:',
          organizationId,
        );
      },
    });
  }

  onAvatarSelected(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    if (input.files && input.files[0] && this.organizationId) {
      const file: File = input.files[0];
      const formData: FormData = new FormData();
      formData.append('Avatar', file);
      formData.append('OrganizationId', this.organizationId);

      this.isOverlayLoading = true;

      this.organizationService.uploadAvatar(formData).subscribe({
        next: (data: BaseApiResponse<OrganizationAvatarUploadResponse>) => {
          this.isOverlayLoading = false;
          input.value = '';
          if (this.organizationId) {
            this.loadOrganizationAvatar(this.organizationId);
          }
        },
        error: () => {
          this.isOverlayLoading = false;
          input.value = '';
        },
      });
    }
  }
}
